// Warden + sculk shrieker (M22.1). PURE (ENG-1): the shrieker's warning ladder
// that summons a warden, and the warden's anger table (who it hunts), emerge gate,
// and armor-piercing sonic boom. The warden is blind — it targets whatever has
// angered it most, not whatever it can see.
import { WARDEN } from '../config';

// ── Sculk shrieker warning ladder ──────────────────────────────────────────────

/** Next warning level after a counted shriek (0 → 1 → 2 → 3, capped). */
export function nextWarningLevel(current: number): number {
  return Math.min(WARDEN.WARNING_MAX, Math.max(0, Math.floor(current)) + 1);
}

/** A shriek at this warning level summons a warden (the top of the ladder). */
export function shouldSummonWarden(warningLevel: number): boolean {
  return Math.floor(warningLevel) >= WARDEN.SUMMON_LEVEL;
}

// ── Warden anger table ─────────────────────────────────────────────────────────

/** A per-warden ledger of how angry it is at each suspect (player or mob id). */
export type AngerLedger = Map<number, number>;

/** Raise anger toward a suspect, capped. Returns the new anger. */
export function addAnger(ledger: AngerLedger, suspect: number, amount: number): number {
  const next = Math.min(WARDEN.ANGER_MAX, (ledger.get(suspect) ?? 0) + amount);
  ledger.set(suspect, next);
  return next;
}

export function angerOf(ledger: AngerLedger, suspect: number): number {
  return ledger.get(suspect) ?? 0;
}

/** The suspect the warden is angriest at (its target), or null if all are calm. */
export function topSuspect(ledger: AngerLedger): { suspect: number; anger: number } | null {
  let best: { suspect: number; anger: number } | null = null;
  for (const [suspect, anger] of ledger) {
    if (anger <= 0) continue;
    if (!best || anger > best.anger || (anger === best.anger && suspect < best.suspect)) best = { suspect, anger };
  }
  return best;
}

/** Decay every suspect's anger by ANGER_DECAY (per second), dropping the calm ones. */
export function decayAnger(ledger: AngerLedger): void {
  for (const [suspect, anger] of ledger) {
    const next = anger - WARDEN.ANGER_DECAY;
    if (next <= 0) ledger.delete(suspect);
    else ledger.set(suspect, next);
  }
}

/** A warden with no remaining anger toward anyone digs away and despawns. */
export function wardenPacified(ledger: AngerLedger): boolean {
  return topSuspect(ledger) === null;
}

/** Anger past this makes the warden actively hunt (below it, it only investigates). */
export function isAngry(anger: number): boolean {
  return anger >= WARDEN.ANGRY_THRESHOLD;
}

/** What a warden is currently doing about its ledger: nothing, investigating the
 *  loudest disturbance, or actively hunting it down. The `suspect` is whatever
 *  caused the noise — a mob just as readily as the player, which is the point of a
 *  blind hunter that tracks sound rather than sight. */
export interface WardenQuarry {
  suspect: number;
  anger: number;
  hunting: boolean;
}

export function wardenQuarry(ledger: AngerLedger): WardenQuarry | null {
  const top = topSuspect(ledger);
  return top ? { ...top, hunting: isAngry(top.anger) } : null;
}

// ── Emerge + sonic boom ─────────────────────────────────────────────────────────

/** The warden finishes emerging (can move/act) once its emerge timer runs out. */
export function wardenEmerged(emergeTicksLeft: number): boolean {
  return emergeTicksLeft <= 0;
}

/**
 * Whether a sonic boom fired from `(sx,sy,sz)` toward a target at `(tx,ty,tz)`
 * connects: the target must be within SONIC_RANGE (the boom travels as a line, so
 * any target in range along the aim is hit). Deterministic distance check.
 */
export function sonicBoomHits(sx: number, sy: number, sz: number, tx: number, ty: number, tz: number): boolean {
  return Math.hypot(tx - sx, ty - sy, tz - sz) <= WARDEN.SONIC_RANGE;
}

/** Sonic-boom damage. It ignores armour and shields entirely (WARDEN.SONIC_BYPASS). */
export function sonicBoomDamage(): number {
  return WARDEN.SONIC_DAMAGE;
}
